import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import CategoryManager from './CategoryManager';
import TemplateForm from './TemplateForm';
import { Settings, ArrowLeft, Plus, Edit2, Trash2, Folder } from 'lucide-react';

export default function TemplateManagement() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null); // eslint-disable-line no-unused-vars
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState('');
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set());
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState([]);

  const loadTemplates = useCallback(async () => {
    if (!user) return;
    
    try {
      let query = supabase
        .from('email_templates')
        .select('*, template_categories!email_templates_category_id_fkey(id, name, color)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      // Apply category filter if selected
      if (selectedCategoryId) {
        query = query.eq('category_id', selectedCategoryId);
      }
      
      const { data, error } = await query;

      if (error) {
        console.error('Error loading templates:', error);
        return;
      }

      if (data) {
        setTemplates(data);
      }
    } catch (err) {
      console.error('Unexpected error loading templates:', err);
    }
  }, [user, selectedCategoryId]);

  const loadCategories = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('template_categories')
        .select('*')
        .eq('user_id', user.id)
        .order('name');

      if (error) {
        console.error('Error loading categories:', error);
        return;
      }

      if (data) {
        setCategories(data);
      }
    } catch (err) {
      console.error('Unexpected error loading categories:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTemplates();
      loadCategories();
    }
  }, [user, loadTemplates, loadCategories]);

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  const handleTemplateCreate = () => {
    setEditingTemplate(null);
    setShowTemplateForm(true);
  };

  const handleTemplateEdit = (template) => {
    setEditingTemplate(template);
    setSelectedTemplate(template);
    setShowTemplateForm(true);
  };

  const handleTemplateDelete = async (template) => {
    if (!window.confirm(`「${template.name}」を削除しますか？`)) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', template.id);

      if (error) {
        console.error('Delete error:', error);
        alert('削除中にエラーが発生しました: ' + error.message);
        return;
      }

      loadTemplates();
      setMessage('テンプレートを削除しました');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Unexpected error during delete:', err);
      alert('予期しないエラーが発生しました: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleTemplateSave = async (savedTemplate) => {
    try {
      setShowTemplateForm(false);
      await loadTemplates();
      
      if (savedTemplate) {
        setSelectedTemplate(savedTemplate);
      }
      
      setEditingTemplate(null);
      setMessage('テンプレートを保存しました');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error loading templates after save:', error);
      setShowTemplateForm(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleMultiSelectToggle = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    setSelectedTemplateIds(new Set());
  };

  const handleTemplateSelect = (templateId) => {
    const newSelected = new Set(selectedTemplateIds);
    if (newSelected.has(templateId)) {
      newSelected.delete(templateId);
    } else {
      newSelected.add(templateId);
    }
    setSelectedTemplateIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTemplateIds.size === templates.length) {
      setSelectedTemplateIds(new Set());
    } else {
      setSelectedTemplateIds(new Set(templates.map(t => t.id)));
    }
  };

  const handleBulkCategoryUpdate = async (categoryId) => {
    if (selectedTemplateIds.size === 0) return;
    
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({ category_id: categoryId })
        .in('id', Array.from(selectedTemplateIds))
        .eq('user_id', user.id);

      if (error) {
        console.error('Bulk update error:', error);
        alert('カテゴリ変更中にエラーが発生しました: ' + error.message);
        return;
      }

      await loadTemplates();
      setMessage(`${selectedTemplateIds.size}件のテンプレートのカテゴリを変更しました`);
      setTimeout(() => setMessage(''), 3000);
      setSelectedTemplateIds(new Set());
      setIsMultiSelectMode(false);
      setShowCategoryModal(false);
    } catch (err) {
      console.error('Unexpected error during bulk update:', err);
      alert('予期しないエラーが発生しました: ' + err.message);
    }
  };

  if (showTemplateForm) {
    return (
      <TemplateForm
        user={user}
        template={editingTemplate}
        onSave={handleTemplateSave}
        onCancel={() => setShowTemplateForm(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl shadow-2xl border-b border-white/20 sticky top-0 z-40 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToDashboard}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="ダッシュボードに戻る"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center group">
                <div className="relative">
                  <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full opacity-20 group-hover:opacity-30 transition-opacity duration-300 blur-sm"></div>
                  <img src="/favicon.png" alt="SalesHub" className="relative w-8 h-8 mr-3" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent">
                    テンプレート管理
                  </h1>
                  <p className="text-xs text-gray-500 font-medium">営業メールテンプレートの設定</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="group flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100/50 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200 border border-gray-200/50 hover:border-red-200"
            >
              <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              ログアウト
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex-1 w-full">
        {message && (
          <div className="mb-8 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50 rounded-2xl shadow-lg backdrop-blur-sm">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg mr-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-green-800 font-medium">{message}</span>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Categories */}
          <div className="lg:col-span-1">
            <CategoryManager
              onCategoryChange={setSelectedCategoryId}
              selectedCategoryId={selectedCategoryId}
            />
          </div>

          {/* Main Content - Templates */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-500" />
                  テンプレート一覧
                </h2>
                <div className="flex items-center gap-2">
                  {templates.length > 0 && (
                    <button
                      onClick={handleMultiSelectToggle}
                      className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                        isMultiSelectMode 
                          ? 'border-purple-600 text-purple-600 bg-purple-50 hover:bg-purple-100' 
                          : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      {isMultiSelectMode ? '選択モード終了' : '複数選択'}
                    </button>
                  )}
                  
                  {isMultiSelectMode && (
                    <>
                      <button
                        onClick={handleSelectAll}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        {selectedTemplateIds.size === templates.length ? '選択解除' : '全て選択'}
                      </button>
                      
                      {selectedTemplateIds.size > 0 && (
                        <button
                          onClick={() => setShowCategoryModal(true)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          カテゴリ変更 ({selectedTemplateIds.size}件)
                        </button>
                      )}
                    </>
                  )}
                  
                  <button
                    onClick={handleTemplateCreate}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    新規作成
                  </button>
                </div>
              </div>

              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <Folder className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">テンプレートがありません</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {selectedCategoryId ? '選択したカテゴリーにはテンプレートがありません。' : '新規作成ボタンから最初のテンプレートを作成しましょう。'}
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleTemplateCreate}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      テンプレートを作成
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {isMultiSelectMode && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectedTemplateIds.size === templates.length && templates.length > 0}
                              onChange={handleSelectAll}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                          </th>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          テンプレート名
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          カテゴリー
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          作成日
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          文字数
                        </th>
                        <th className="relative px-6 py-3">
                          <span className="sr-only">操作</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {templates.map((template) => (
                        <tr key={template.id} className="hover:bg-gray-50 transition-colors">
                          {isMultiSelectMode && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedTemplateIds.has(template.id)}
                                onChange={() => handleTemplateSelect(template.id)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {template.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {template.template_categories ? (
                              <span 
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${template.template_categories.color}20`,
                                  color: template.template_categories.color 
                                }}
                              >
                                {template.template_categories.name}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">なし</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(template.created_at).toLocaleDateString('ja-JP')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {template.content.length} 文字
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleTemplateEdit(template)}
                              className="text-indigo-600 hover:text-indigo-900 mr-3"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleTemplateDelete(template)}
                              disabled={deleting}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/favicon.png" alt="SalesHub" className="w-5 h-5" />
              <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">SalesHub</span> - 営業効率化ツール
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Developed by</span>
              <a 
                href="https://lunascent.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200"
              >
                しゅん。
              </a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* カテゴリ変更モーダル */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCategoryModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              カテゴリを選択 ({selectedTemplateIds.size}件のテンプレート)
            </h3>
            <div className="space-y-2">
              <button
                onClick={() => handleBulkCategoryUpdate(null)}
                className="w-full p-3 text-left rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-gray-400" />
                  <span className="font-medium text-gray-900">未分類</span>
                </div>
              </button>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleBulkCategoryUpdate(category.id)}
                  className="w-full p-3 text-left rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="font-medium text-gray-900">{category.name}</span>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowCategoryModal(false)}
              className="mt-4 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              キャンセル
            </button>
          </div>
        </div>
      )}
    </div>
  );
}